import dayjs from 'dayjs'

import { roundObj } from '../../helpers/roundObj'

const TYPE_MAP = {
  None: 'count', // DEPRECATED: None as label
  Count: 'count',
  Percent: 'percentage',
}

// https://en.wikipedia.org/wiki/Largest_remainder_method
export const largestRemainderRound = (values, desiredSum = 100) => {
  if (values.length === 0) return values

  let sum = 0
  let valueParts = values.map((value, index) => {
    // Get rounded down integer values.
    let integerValue = value | 0
    sum += integerValue
    return {
      integer: integerValue,
      decimal: value % 1,
      originalIndex: index,
    }
  })

  if (sum != desiredSum) {
    // Sort values by decimal part
    valueParts = valueParts.sort((a, b) => b.decimal - a.decimal)

    const diff = desiredSum - sum
    let i = 0

    // Distribute the difference.
    while (i < diff) {
      valueParts[i % valueParts.length].integer++
      i++
    }
  }

  return valueParts.sort((a, b) => a.originalIndex - b.originalIndex).map((p) => p.integer)
}

const getMonthWithYear = (date) => dayjs(date).format('MMM-YYYY')

export const formatAudienceTrend = ({ raw, timeFrame, selectedEvents, type }) => {
  if (!raw || !timeFrame || !selectedEvents || !type) return []

  const EVENTS_MAP = {
    clicks: 'Clicks',
    searches: 'Searches',
    shares: 'Shares',
    page_views: 'Page Views',
    total: 'Total events',
  }

  const timeKey = timeFrame.replace('_of_year', '')
  const filteredEvents = Object.entries(selectedEvents)
    .filter(([_, value]) => value)
    .map(([key]) => key.replace('_events', ''))
  const typeKey = TYPE_MAP[type]

  const formatted = Object.entries(raw[timeKey]).map(([date, events]) => ({
    date,
    month: getMonthWithYear(date),
    values: Object.fromEntries(
      filteredEvents.map((event) => [EVENTS_MAP[event], Number(events?.[event]?.[typeKey] ?? 0)])
    ),
  }))

  return formatted
}

export const formatAudienceComparison = ({ raw, timeFrame, type }) => {
  if (!raw?.length || !timeFrame || !type) return []

  const typeKey = TYPE_MAP[type]
  const timeKey = timeFrame.replace('_of_year', '')

  const preFormat = raw.map((item) => ({
    name: item.entity.name.split('>').at(-1).trim(),
    data: Object.entries(item.data[timeKey])
      .map(([date, values]) => [date, values['total'][typeKey]])
      .sort((a, b) => a[0].localeCompare(b[0])),
  }))

  const baseItem = preFormat[0].data
  const entities = preFormat.map(({ name }) => name)
  const formatted = baseItem.map((_, index) => ({
    date: baseItem[index][0],
    month: getMonthWithYear(baseItem[index][0]),
    values: Object.fromEntries(entities.map((entity, i) => [entity, preFormat[i].data[index][1]])),
  }))

  return formatted
}

export const formatKECC = ({ raw, names, timeFrame, type }) => {
  if (!raw || !names?.length || !timeFrame || !type) return []

  const normalize = (name) => name.replaceAll(' ', '_').toLowerCase()

  const typeKey = TYPE_MAP[type]
  const timeKey = timeFrame.replace('_of_year', '')

  const preFormat = Object.fromEntries(
    Object.entries(raw).map(([name, data]) => [
      name,
      Object.entries(data[timeKey])
        .map(([date, values]) => [date, Number(values[typeKey])])
        .sort((a, b) => a[0].localeCompare(b[0])),
    ])
  )

  const dates = Object.values(preFormat)
    .at(0)
    .map(([date]) => date)

  const formatted = dates.map((date, index) => ({
    date: date,
    month: getMonthWithYear(date),
    values: Object.fromEntries(
      names.map((name) => [name, preFormat?.[normalize(name)]?.[index]?.[1] ?? null])
    ),
  }))

  return formatted
}

export const INCOMES_MAP = {
  lt_30k: '$0-29K',
  '30k_50k': '$30K-49K',
  '50k_74k': '$50K-74K',
  '75k_99k': '$75K-99K',
  '100k_150k': '$100K-150K',
  gt_150k: '+$150K',
}

export const GENDERS_MAP = {
  female: 'Female',
  male: 'Male',
}

export const AGES_MAP = {
  '18_24': '18-24 years',
  '25_34': '25-34 years',
  '35_44': '35-44 years',
  '45_54': '45-54 years',
  '55_64': '55-64 years',
  gt_65: '+65 years',
}

export const formatIncomeBy = (data, l1Labels, l2Labels) => {
  if (!data) return null

  const l1SortMap = l1Labels && Object.values(l1Labels)
  const l2SortMap = l2Labels && Object.values(l2Labels)

  const l1Obj = Object.fromEntries(
    Object.entries(data).map(([key, value]) => [key, value?.distribution ?? value])
  )
  const l1Round = roundObj(l1Obj)

  const roundL2 = Object.entries(data).map(([key, l2Obj]) => {
    const { distribution, ..._l2Data } = l2Obj
    const l2Data = roundObj(Object.values(_l2Data).at(0))

    if (l2Labels) {
      const entries = Object.entries(l2Data).map(([l2Key, l2Val]) => ({
        name: l2Labels[l2Key],
        value: l2Val,
      }))
      const sortedL2 = [...entries].sort(
        (a, b) => l2SortMap.indexOf(a.name) - l2SortMap.indexOf(b.name)
      )
      return {
        name: l1Labels[key],
        value: l1Round[key],
        entries: sortedL2,
      }
    } else {
      return {
        name: l1Labels[key],
        value: l1Round[key],
      }
    }
  })

  const sorted = [...roundL2].sort((a, b) => l1SortMap.indexOf(a.name) - l1SortMap.indexOf(b.name))

  return sorted
}
